import { DndContext, type DragEndEvent, DragOverlay, type DragStartEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { Box, type Theme } from "@mui/material";
import type { DateTime } from "luxon";
import { Fragment, memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CoverageSectionHeader } from "../../../components/UIToolkit/Coverage/CoverageSectionHeader";
import { NameChip } from "../../../components/UIToolkit/Coverage/NameChip";
import { NEUTRAL } from "../../../theme/tokens";
import type { CoverageEditWiring } from "./CoverageGrid";
import { type CoverageGridContextValue, CoverageGridProvider, type GridCellKey, serializeGridCell } from "./grid/CoverageGridContext";
import { type DragData, filledCount } from "./grid/DetailCell";
import { DetailRowDays } from "./grid/DetailRowDays";
import { DetailRowIdentity } from "./grid/DetailRowIdentity";
import { GridDayHeaderRow } from "./grid/GridDayHeaderRow";
import { GridScrollContainer } from "./grid/GridScrollContainer";
import { GridWeekHeaderRow } from "./grid/GridWeekHeaderRow";
import { StickyLeadingCell } from "./grid/StickyLeadingCell";
import { useCellPopover } from "./grid/useCellPopover";
import { useLinkedHover } from "./grid/useLinkedHover";
import { useToggleSet } from "./grid/useToggleSet";
import { weekHoverTintRules } from "./grid/weekHoverTint";
import { gridColumns, leadingOffsets, ROW_H, totalTracks } from "./gridGeometry";
import { CoverageLocationCreate } from "./LocationConfigEditor/CoverageLocationCreate";
import { CoverageRecurringShiftEditor, type RecurringShiftEditorRequest } from "./RecurringShiftEditor/CoverageRecurringShiftEditor";
import { type AssignedName, type CoverageDayCell, type CoverageSection, computeSectionRollup, type CoverageRow as RowType } from "./types";

// Detail uses the COMPACT leading widths — the leading zone (Location/Shift/Role)
// shrinks so the freed width goes to the name chips, which are what matters here.
const { shift: SHIFT_LEFT, role: ROLE_LEFT } = leadingOffsets("compact");

type Props = {
	sections: CoverageSection[];
	weeks: number;
	/** First day of week 1 (cycle anchor) — drives day/week header dates. */
	cycleStart: DateTime;
	/**
	 * When supplied (live mount), clicking a name chip edits that nurse's recurring
	 * shift and the +Add open-slot creates one. Omitted in mock / prototype renders.
	 */
	editWiring?: CoverageEditWiring;
	/** Job-managed dept: the identity column reads each row's `jobName` (backend-supplied) and its
	    header label switches to "Job". Non-job depts (false) keep the plain Role column. */
	isJobDept?: boolean;
};

/** A thin, memoized Detail row: the compact identity block + the day-cell loop. */
const DetailRow = memo(({ row, draggingId, isDragging }: { row: RowType; draggingId?: string; isDragging: boolean }) => (
	// `display: contents` carries the ARIA row without adding a box to the CSS grid.
	<Box role="row" sx={{ display: "contents" }}>
		<DetailRowIdentity row={row} />
		<DetailRowDays row={row} draggingId={draggingId} isDragging={isDragging} />
	</Box>
));

export const CoverageDetailGrid = ({ sections: initialSections, weeks, cycleStart, editWiring, isJobDept }: Props) => {
	const { t } = useTranslation();
	// Copy/move edits live here (no backend). State is treated as immutable — onDragEnd
	// rebuilds only the touched path and spreads everything else through by reference — so
	// we never mutate the imported mock and can seed directly from it (no upfront clone).
	const [sections, setSections] = useState<CoverageSection[]>(initialSections);
	// Re-seed when the incoming (filtered) sections change — otherwise a filter change
	// applied while the Detail quadrant is mounted is ignored (local drag edits are
	// ephemeral, so resetting to the new dataset is the correct behavior).
	const [seededFrom, setSeededFrom] = useState<CoverageSection[]>(initialSections);
	if (seededFrom !== initialSections) {
		setSeededFrom(initialSections);
		setSections(initialSections);
	}
	const [collapsed, toggle] = useToggleSet<string>();
	// Single-open cell popover: owns the open cell, the open-deferral hack, and the
	// closing cell kept mounted through its exit animation (Phase B).
	const popover = useCellPopover<GridCellKey>(serializeGridCell);
	const [active, setActive] = useState<DragData | null>(null);
	// Linked hover is CSS-driven (no React state / no grid re-render).
	const { gridRef, setHovered } = useLinkedHover();

	// One recurring-shift editor request at a time (fixed-position modal → grid level).
	const [recurringRequest, setRecurringRequest] = useState<{ request: RecurringShiftEditorRequest; clickedDate: DateTime } | null>(null);
	// Section-header "+" create: the clicked section's name (null = closed). Mounted
	// only while open so each open starts fresh (pre-seeded with this section).
	const [createSection, setCreateSection] = useState<string | null>(null);

	const onOpenRecurring = useCallback(
		(request: RecurringShiftEditorRequest, dayIdx: number) => {
			popover.close();
			setRecurringRequest({ request, clickedDate: cycleStart.plus({ days: dayIdx }) });
		},
		[popover.close, cycleStart],
	);
	// Shift held at the moment of drop → MOVE; otherwise COPY (the default).
	const [moveMode, setMoveMode] = useState(false);

	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

	const tracks = totalTracks(weeks);
	const dayDates = useMemo(() => Array.from({ length: weeks * 7 }, (_, i) => cycleStart.plus({ days: i })), [weeks, cycleStart]);

	const gridValue = useMemo<CoverageGridContextValue>(
		() => ({ weeks, cycleStart, dayDates, displayMode: "delta", editWiring, popover, onOpenRecurring, setHoveredNurse: setHovered }),
		[weeks, cycleStart, dayDates, editWiring, popover, onOpenRecurring, setHovered],
	);

	const findCell = (list: CoverageSection[], key: GridCellKey) => {
		for (const s of list) {
			const row = s.rows.find((r) => r.id === key.rowId);
			if (row) return row.days[key.dayIdx];
		}
		return undefined;
	};

	const onDragStart = (e: DragStartEvent) => {
		const data = e.active.data.current as DragData | undefined;
		if (data) setActive(data);
		popover.close();
		// Clear any linked hover so it doesn't fight the drag.
		setHovered(null);
		setMoveMode((e.activatorEvent as PointerEvent | undefined)?.shiftKey ?? false);
	};

	const onDragEnd = (e: DragEndEvent) => {
		const data = e.active.data.current as DragData | undefined;
		const overId = e.over?.id as string | undefined;
		const isMove = (e.activatorEvent as PointerEvent | undefined)?.shiftKey || moveMode;
		setActive(null);
		setMoveMode(false);
		if (!data || !overId) return;

		// over id is a cell key; only empty (under-target) slots accept a drop.
		const [rowId, dayIdxStr] = overId.split("::");
		const target: GridCellKey = { rowId, dayIdx: Number(dayIdxStr) };
		// No-op drop onto the originating cell.
		if (target.rowId === data.from.rowId && target.dayIdx === data.from.dayIdx) return;

		setSections((prev): CoverageSection[] => {
			const targetCell = findCell(prev, target);
			if (!targetCell) return prev;
			const openSlots = Math.max(0, targetCell.target - filledCount(targetCell.names));
			if (openSlots <= 0) return prev; // full — reject

			// Add a fresh assignment (new id) so copy + move both produce a unique chip.
			const placed: AssignedName = { ...data.assignment, id: `asg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, onPto: false };

			// Rebuild ONLY the touched path — the affected section(s), the target/source
			// row(s), and just the changed day cell(s). Everything else (untouched sections,
			// rows, days, and name arrays) is spread through by reference so its identity is
			// preserved — no full-tree clone, so downstream memoization stays intact.
			return prev.map((section): CoverageSection => {
				let rowChanged = false;
				const rows = section.rows.map((row): RowType => {
					const isTargetRow = row.id === target.rowId;
					const isSourceRow = isMove && row.id === data.from.rowId;
					if (!isTargetRow && !isSourceRow) return row;
					rowChanged = true;
					const days = row.days.map((cell, di): CoverageDayCell => {
						if (isTargetRow && di === target.dayIdx) {
							const names = [...cell.names, placed];
							return { ...cell, names, filled: filledCount(names) };
						}
						if (isSourceRow && di === data.from.dayIdx) {
							const names = cell.names.filter((n) => n.id !== data.assignment.id);
							return { ...cell, names, filled: filledCount(names) };
						}
						return cell;
					});
					return { ...row, days };
				});
				return rowChanged ? { ...section, rows } : section;
			});
		});
	};

	return (
		<DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setActive(null)}>
			<CoverageGridProvider value={gridValue}>
				<GridScrollContainer
					ariaLabel={t("schedule.template.v3.grid.coverageDetailAria")}
					dataTestId="ms3-coverage-detail-grid"
					gridTemplateColumns={gridColumns(weeks, "compact")}
					gridRef={gridRef}
					containerSx={[{ "& .ms3-week-header": { transition: "background-color 150ms" } }, (theme: Theme) => weekHoverTintRules(theme, weeks)]}
				>
					{/* ── Row 1: week headers ── */}
					{/* Same anchor as the Summary grid above — the Detail quadrant never pages off today's week. */}
					<GridWeekHeaderRow weeks={weeks} cycleStart={cycleStart} activeWeekIndex={0} variant="coverage" testIdPrefix="ms3-detail" />

					{/* ── Row 2: leading labels + day-of-week headers ── */}
					<Box role="row" sx={{ display: "contents" }}>
						<StickyLeadingCell left={0} variant="header" role="columnheader" sx={{ top: ROW_H }}>
							{t("schedule.template.v3.col.location")}
						</StickyLeadingCell>
						<StickyLeadingCell left={SHIFT_LEFT} variant="header" role="columnheader" sx={{ top: ROW_H }}>
							{t("schedule.template.v3.col.shift")}
						</StickyLeadingCell>
						<StickyLeadingCell left={ROLE_LEFT} variant="header" role="columnheader" showEdgeShadow sx={{ top: ROW_H }}>
							{t(isJobDept ? "schedule.template.v3.col.job" : "schedule.template.v3.col.role")}
						</StickyLeadingCell>
						<GridDayHeaderRow weeks={weeks} cycleStart={cycleStart} variant="compact" />
					</Box>

					{/* ── Sections ── */}
					{sections.map((section) => {
						const { fillPct, gapCount } = computeSectionRollup(section);
						const isCollapsed = collapsed.has(section.id);
						const canCreate = !!editWiring && !editWiring.editBlocked && !!section.name;
						const sectionHeader = (
							<CoverageSectionHeader
								name={section.name}
								fillPct={fillPct}
								gapCount={gapCount}
								isCollapsed={isCollapsed}
								onToggle={() => toggle(section.id)}
								gridColumn={`1 / span ${tracks}`}
								noTargetsLabel={t("schedule.template.v3.section.noTargets")}
								fillLabel={fillPct === null ? "" : t("schedule.template.v3.section.fill", { pct: fillPct })}
								gapsLabel={t("schedule.template.v3.section.gaps", { count: gapCount })}
								toggleAriaLabel={t("schedule.template.v3.section.toggleAria", { name: section.name })}
								onAdd={canCreate ? () => setCreateSection(section.name) : undefined}
								addLabel={t("schedule.template.v3.section.addLocation", { section: section.name })}
								addDataTestId={`ms3-detail-section-add-${section.id}`}
								dataTestId={`ms3-detail-section-${section.id}`}
							/>
						);
						return (
							<Fragment key={section.id}>
								{/* Full-width section band = one ARIA row whose single cell is a rowheader. */}
								<Box role="row" sx={{ display: "contents" }}>
									<Box role="rowheader" sx={{ display: "contents" }}>
										{editWiring && canCreate ? (
											<CoverageLocationCreate
												open={createSection === section.name}
												onClose={() => setCreateSection(null)}
												weekStartDay={editWiring.weekStartDay}
												uniqueSections={editWiring.uniqueSections}
												initialSection={section.name}
												onCoverageRefresh={editWiring.onCoverageRefresh}
											>
												{sectionHeader}
											</CoverageLocationCreate>
										) : (
											sectionHeader
										)}
									</Box>
								</Box>

								{!isCollapsed && section.rows.map((row) => <DetailRow key={row.id} row={row} draggingId={active?.assignment.id} isDragging={!!active} />)}
							</Fragment>
						);
					})}
				</GridScrollContainer>
			</CoverageGridProvider>

			<DragOverlay dropAnimation={null}>
				{active ? (
					<NameChip
						name={active.assignment.name}
						role={active.assignment.role}
						charge={active.assignment.charge}
						chargeLabel={t("schedule.template.v3.detail.charge")}
						ot={active.assignment.ot}
						otLabel={t("schedule.template.v3.people.otBadge")}
						showDragHandle
						sx={{ boxShadow: `0 6px 16px ${NEUTRAL[500]}`, cursor: "grabbing" }}
					/>
				) : null}
			</DragOverlay>

			{editWiring && recurringRequest && (
				<CoverageRecurringShiftEditor
					request={recurringRequest.request}
					clickedDate={recurringRequest.clickedDate}
					onCoverageRefresh={editWiring.onCoverageRefresh}
					onClose={() => setRecurringRequest(null)}
				/>
			)}
		</DndContext>
	);
};
